// src/components/ContactDeleteConfirmModal/index.js
import React, { useState, useContext } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { Alert } from "@material-ui/lab";
import ColorModeContext from "../../layout/themeContext";
import { createGradientStyles } from "../../utils/themeColors";

const useStyles = makeStyles((theme) => ({
  // ✅ Estilos modernos do modal
  dialogTitle: {
    padding: theme.spacing(2, 3),
    flexShrink: 0,
    color: '#fff',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '1.2rem',
      color: 'inherit',
    }
  },
  ...createGradientStyles(theme),
  dialogContent: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#fafafa',
    overflowY: 'auto',
    overflowX: 'hidden',
    flex: '1 1 auto',
    maxHeight: 'calc(100vh - 250px)',
    minWidth: 400,
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.type === 'dark' ? '#2a2a2a' : '#f1f1f1',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.type === 'dark' ? '#555' : '#888',
      borderRadius: '4px',
      '&:hover': {
        background: theme.palette.type === 'dark' ? '#777' : '#555',
      },
    },
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f5f5f5',
    borderTop: theme.palette.type === 'dark' ? '1px solid #444' : '1px solid #e0e0e0',
    flexShrink: 0,
    gap: theme.spacing(1),
  },
  confirmationInput: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#ffffff',
      '&:hover': {
        backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#f9f9f9',
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.type === 'dark' ? '#2a2a2a' : '#ffffff',
      },
    },
  },
  warningText: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: theme.palette.error.main,
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  buttonPrimary: {
    background: 'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
    color: '#fff',
    fontWeight: 600,
    padding: theme.spacing(1, 3),
    boxShadow: '0 4px 15px 0 rgba(244, 67, 54, 0.4)',
    '&:hover': {
      background: 'linear-gradient(135deg, #d32f2f 0%, #f44336 100%)',
      boxShadow: '0 6px 20px 0 rgba(244, 67, 54, 0.5)',
      transform: 'translateY(-1px)',
    },
    '&:disabled': {
      background: theme.palette.type === 'dark' ? '#444' : '#ccc',
      color: theme.palette.type === 'dark' ? '#888' : '#666',
      boxShadow: 'none',
    },
    transition: 'all 0.3s ease',
  },
  buttonSecondary: {
    color: theme.palette.type === 'dark' ? '#fff' : theme.palette.text.primary,
    fontWeight: 500,
    padding: theme.spacing(1, 3),
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
    },
  },
}));

const ContactDeleteConfirmModal = ({
  open,
  onClose,
  onConfirm,
  deleteType, // 'selected' ou 'all'
  selectedCount,
  totalCount,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const colorModeContext = useContext(ColorModeContext);
  const useGradient = colorModeContext?.useGradient || theme.palette?.gradient || false;
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState("");

  const expectedText = deleteType === 'all' ? 'EXCLUIR TODOS' : 'EXCLUIR SELECIONADOS';
  const isConfirmValid = confirmText === expectedText;

  const handleConfirm = () => {
    if (!isConfirmValid) {
      setError(`Digite exatamente: ${expectedText}`);
      return;
    }
    
    setConfirmText("");
    setError("");
    onConfirm();
  };

  const handleClose = () => {
    setConfirmText("");
    setError("");
    onClose();
  };

  const getTitle = () => {
    if (deleteType === 'all') {
      return `Excluir TODOS os ${totalCount} contatos?`;
    }
    return `Excluir ${selectedCount} contatos selecionados?`;
  };

  const getMessage = () => {
    if (deleteType === 'all') {
      return (
        <>
          <Alert severity="error" className={classes.alert}>
            ⚠️ ATENÇÃO: Esta ação irá excluir TODOS os {totalCount} contatos da empresa permanentemente!
          </Alert>
          <Typography variant="body2">
            Esta operação não pode ser desfeita. Todos os contatos, seus históricos de mensagens, 
            tags e relacionamentos serão perdidos definitivamente.
          </Typography>
        </>
      );
    }
    
    return (
      <>
        <Alert severity="warning" className={classes.alert}>
          Esta ação irá excluir {selectedCount} contatos selecionados permanentemente.
        </Alert>
        <Typography variant="body2">
          Os contatos selecionados e seus dados relacionados serão excluídos definitivamente.
        </Typography>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="delete-confirm-dialog"
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        style: {
          backgroundColor: theme.palette.type === 'dark' ? '#1e1e1e' : '#ffffff',
          borderRadius: isMobile ? 0 : theme.shape.borderRadius,
        }
      }}
    >
      <DialogTitle 
        id="delete-confirm-dialog"
        className={`${classes.dialogTitle} ${useGradient ? classes.gradientBackground : classes.solidBackground}`}
      >
        {getTitle()}
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {getMessage()}
        
        <Typography variant="body1" className={classes.warningText}>
          Para confirmar, digite exatamente: <strong>{expectedText}</strong>
        </Typography>
        
        <TextField
          fullWidth
          label="Confirmação"
          value={confirmText}
          onChange={(e) => {
            setConfirmText(e.target.value);
            setError("");
          }}
          placeholder={expectedText}
          className={classes.confirmationInput}
          error={!!error}
          helperText={error}
          autoFocus
        />
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button onClick={handleClose} className={classes.buttonSecondary} variant="text">
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          className={classes.buttonPrimary}
          variant="contained"
          disabled={!isConfirmValid}
        >
          {deleteType === 'all' ? 'Excluir Todos' : 'Excluir Selecionados'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactDeleteConfirmModal;